import React, {
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import { Collapse, Row, Spin } from 'antd';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import BoardResultsList from './BoardResultsList';

import BoardCardDrawer from '../boards/BoardCardDrawer';
import useBoardPermissions from '../common/hooks/useBoardPermissions';
import ShortListCardDrawer from '../boards/ShortLists/ShortListCardDrawer';
import CustomFieldFormMappingDrawer from '../forms/CustomFieldFormMappingDrawer/CustomFieldFormMappingDrawer';
import FormResponder from '../forms/FormResponder';
import { closeFormDrawer, getFormMappings } from '../boards/state/boards.actions';

export default function BoardsResults({
  loading,
  results,
}) {
  const dispatch = useDispatch();
  const boards = useSelector((state) => state.boards.boards);

  const {
    selectedCardId,
  } = useSelector((state) => state.boards.drawerConfig);
  const reduxCard = useSelector((state) => state.boards.selectedCard);
  const boardDetailsMap = useSelector((state) => state.boards.boardDetailsMap);

  const cardTypes = useSelector((state) => state.boards.cardTemplates);

  const latestManualDraftId = useSelector((state) => state.forms.latestManualDraftId);
  const {
    visible: formDrawerVisible = false,
    assignedForm,
    formData,
    cardId: formCardId,
    isEdit: selectedFormIsEdit,
    fileMap,
    ids: preloadedIds,
  } = useSelector((state) => state.boards.formDrawerConfig);
  const {
    visible: formMapDrawerVisible = false,
    selectedCardId: formMapCardId,
    boardId: formMapBoardId,
  } = useSelector((state) => state.boards.formMapDrawerConfig);

  const selectedCard = reduxCard?.id === selectedCardId ? reduxCard : null;
  const {
    boardId: selectedBoardId,
  } = selectedCard ?? {};

  const realBoardId = formMapBoardId ?? selectedBoardId;

  const boardDetails = boardDetailsMap[realBoardId] || {};
  const board = boards[realBoardId] || {};

  const {
    showCardNumber,
    cardTypeId,
    divisions,
    creatorId,
  } = board;

  const {
    users = [],
  } = boardDetails;
  const cardType = cardTypes?.[cardTypeId] ?? {};

  const assignedDraft = useMemo(() => {
    const relevantDraftId = latestManualDraftId ?? assignedForm?.draftId;
    if (!assignedForm) return { draftId: relevantDraftId };
    return {
      ...assignedForm,
      draftId: relevantDraftId,
    };
  }, [assignedForm, latestManualDraftId]);

  const hideFormDrawer = useCallback(() => dispatch(closeFormDrawer()), [dispatch]);

  useEffect(() => {
    if (realBoardId) {
      dispatch(getFormMappings(realBoardId));
    }
  }, [realBoardId]);

  const cardsByBoardAndStatus = useMemo(() => (
    results.reduce((acc, card) => {
      if (!acc[card.boardId]) acc[card.boardId] = { resultsCount: 0 };
      if (!acc[card.boardId][card.statusId]) acc[card.boardId][card.statusId] = [];
      acc[card.boardId][card.statusId].push(card);
      acc[card.boardId].resultsCount += 1;
      return acc;
    }, {})
  ), [results]);

  const userBoardPermissions = useBoardPermissions({ users, creatorId });

  const boardResults = useMemo(() => (
    Object.entries(cardsByBoardAndStatus).map(([boardId, boardStatuses = {}]) => (
      <Collapse.Panel
        header={boards[boardId]?.title}
        key={boardId}
        extra={`${cardsByBoardAndStatus[boardId].resultsCount} Results`}
        collapsible="header"
        style={{ width: '100%' }}
      >
        <BoardResultsList
          boardId={boardId}
          cardsByStatus={boardStatuses}
        />
      </Collapse.Panel>
    ))
  ), [cardsByBoardAndStatus]);

  return (
    <>
      {
        loading
          ? (
            <div style={{ width: '100%' }}>
              <Row style={{ flex: 1 }} justify="center" align="middle">
                <Spin />
              </Row>
            </div>
          )
          : (
            <Collapse
              size="large"
              style={{ width: '100%' }}
            >
              {boardResults}
            </Collapse>
          )
      }
      <BoardCardDrawer
        boardId={selectedBoardId}
        cardTypeId={cardTypeId}
        divisions={divisions}
        showCardNumber={showCardNumber}
        useCardNumberAsCardTitle={cardType?.useCardNumberAsCardTitle}
        userBoardPermissions={userBoardPermissions}
      />
      <CustomFieldFormMappingDrawer
        visible={formMapDrawerVisible}
        selectedProfileId={formMapCardId}
        profileTypeId={cardTypeId}
        divisions={divisions}
        type="card"
      />
      <FormResponder
        visible={formDrawerVisible}
        onClose={hideFormDrawer}
        assignedForm={assignedDraft}
        preloadedData={formData}
        preloadedIds={preloadedIds}
        isOnEditStep={selectedFormIsEdit}
        cardId={formCardId}
        initialFileMap={fileMap}
      />
      <ShortListCardDrawer />
    </>
  );
}

BoardsResults.propTypes = {
  loading: PropTypes.bool,
  results: PropTypes.arrayOf(PropTypes.shape({})),
};

BoardsResults.defaultProps = {
  loading: false,
  results: [],
};
